import { useState, useEffect } from 'react';
import useMeeting from './useMeeting';
import { Suggestion, Nudge, PanelType } from 'types/common';
import { POLLING_INTERVAL, SUGGESTION_TYPES, NUDGE_TYPES } from '../configs/config';
import { notificationService } from './useNotificationService';

/**
 * Custom hook for managing suggestions and nudges
 * @param panelType - Type of panel to show ('all', 'suggestions', or 'nudges')
 * @returns Object containing suggestions and nudges
 */
export const useNotifications = (panelType: PanelType = 'all'): {
    suggestions: Suggestion[];
    nudges: Nudge[];
    isLoading: boolean;
} => {
    const { classId } = useMeeting();
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [nudges, setNudges] = useState<Nudge[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Set up listeners and start polling
    useEffect(() => {
        if (!classId) return;

        setIsLoading(true);

        // Configure the service
        notificationService.configure({
            suggestionTypesStr: SUGGESTION_TYPES,
            nudgeTypesStr: NUDGE_TYPES,
            pollingInterval: POLLING_INTERVAL
        });

        // Create listeners based on panel type
        const listener = {
            onSuggestions: (newSuggestions: Suggestion[]) => {
                if (panelType === 'all' || panelType === 'suggestions') {
                    setSuggestions(newSuggestions);
                    setIsLoading(false);
                }
            },
            onNudges: (newNudges: Nudge[]) => {
                if (panelType === 'all' || panelType === 'nudges') {
                    setNudges(newNudges);
                    setIsLoading(false);
                }
            }
        };

        // Add listener and start polling
        notificationService.addListener(listener).startPolling(classId);

        // Clean up
        return () => {
            notificationService.removeListener(listener);
        };
    }, [classId, panelType]);

    return {
        suggestions,
        nudges,
        isLoading
    };
};